import React, { useState } from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18n-js';
import AutocompleteStyled from './Autocomplete.styled';
import Autocomplete from '@material-ui/lab/Autocomplete';
import CircularProgress from '@material-ui/core/CircularProgress';

const AutocompleteSelect = ({
  label,
  variant,
  id,
  options,
  defaultValue,
  onInputChange,
  dataCy,
  name,
  disabled,
  loading,
  getOptionLabel,
  getOptionSelected,
  params, // eslint-disable-line no-unused-vars -- destructured to keep it out of ...props
  error,
  helperText,
  onChange,
  margin,
  classes,
  required,
  customNoOptionText,
  ...props
}) => {
  const [open, setOpen] = useState(false);
  const [customLoading, setCustomLoading] = useState(false);
  const [emptyText, setEmptyText] = useState(i18n.t('common.enter_keywords'));
  // `reason` is MUI's third argument ('input' | 'reset' | 'clear'). It is forwarded so consumers
  // can tell real user input from MUI echoing the selected option's label back on 'reset'.
  const onInputChangeCustom = (e, val, reason) => {
    setCustomLoading(true);
    if (onInputChange) {
      onInputChange(e, val, reason);
    }
    if (val.length === 0) {
      setEmptyText(i18n.t('common.enter_keywords'));
    } else if (options.length > 0) {
      setEmptyText(customNoOptionText ? customNoOptionText : i18n.t('common.no_results'));
    } else {
      setEmptyText(customNoOptionText);
    }
    setCustomLoading(false);
  };

  return (
    <Autocomplete
      id={id}
      name={name}
      open={open}
      fullwidth="true"
      loading={loading || customLoading}
      options={options}
      onChange={onChange}
      disabled={disabled}
      value={defaultValue}
      data-cy={dataCy || id}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      getOptionLabel={getOptionLabel}
      onInputChange={onInputChangeCustom}
      getOptionSelected={getOptionSelected}
      noOptionsText={emptyText}
      filterOptions={(x) => x}
      clearOnBlur={true}
      {...props}
      renderInput={(params) => (
        <AutocompleteStyled
          {...params}
          error={error}
          label={label}
          margin={margin}
          variant={variant}
          classes={classes}
          required={required}
          helperText={helperText}
          inputProps={{
            ...(params?.inputProps || {}),
            ...(props?.inputProps || {}),
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.inputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

AutocompleteSelect.propTypes = {
  id: PropTypes.string,
  options: PropTypes.array,
  loading: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.node,
  onChange: PropTypes.func,
  onInputChange: PropTypes.func,
  getOptionLabel: PropTypes.func,
  getOptionSelected: PropTypes.func,
  customNoOptionText: PropTypes.string,
  params: PropTypes.object,
  defaultValue: PropTypes.any,
  classes: PropTypes.any,
  name: PropTypes.string,
  label: PropTypes.string,
  dataCy: PropTypes.string,
  margin: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  inputProps: PropTypes.object,
  variant: PropTypes.oneOf(['standard', 'outlined', 'filled']),
};

AutocompleteSelect.defaultProps = {
  classes: {},
  name: 'AutocompleteSelect',
  required: false,
  disabled: false,
  margin: 'normal',
  variant: 'standard',
  label: 'Select...',
};

export default AutocompleteSelect;
